import { useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { DecisionsBadge, isDecisionsModel } from '@/components/DecisionsBadge';
import {
  CapabilitySwitches, apiErrorMessage, badNumber, needsAttention, parseOptional, parsePrice, type Capabilities
} from '@/components/modelBits';
import { daysAgo, fmtDate, fmtNumber, fmtTokens, fmtUsd, isoDate } from '@/lib/format';
import type { AdminModel } from '@/types';

type Tab = 'overview' | 'pricing' | 'usage';
const tabLabels: Record<Tab, string> = { overview: 'Overview', pricing: 'Pricing', usage: 'Usage' };

/**
 * One model's page: what it accepts, its fallback, its price and its recent usage, with a single
 * Save for everything. There is no audit log behind the catalogue, so "history" is the last change
 * the row records — deliberately not dressed up as a timeline.
 */
export function ModelDetail() {
  const { id } = useParams();
  const api = useApi();
  const models = useQuery({ queryKey: ['admin-models'], queryFn: api.adminListModels });
  const gatewayDefault = useQuery({ queryKey: ['gateway-default'], queryFn: api.adminGetGatewayDefault });
  const [tab, setTab] = useState<Tab>('overview');

  if (models.isLoading) return <div className="placeholder">Loading model...</div>;
  if (models.error) return <div className="error-banner">Could not load models: {(models.error as Error).message}</div>;

  const all = models.data ?? [];
  const model = all.find(m => String(m.id) === id);
  if (!model) {
    return (
      <>
        <div className="crumbs"><Link to="/admin/models">Models</Link></div>
        <div className="placeholder">There's no model with id {id} in the catalogue. It may have been deleted.</div>
      </>
    );
  }

  const isDefault = gatewayDefault.data?.supplier === model.supplier && gatewayDefault.data?.modelId === model.modelId;

  return (
    <ModelEditor
      key={model.id}
      model={model}
      all={all}
      isDefault={isDefault}
      tab={tab}
      onTab={setTab}
    />
  );
}

function str(v: number | null | undefined): string {
  return v == null ? '' : String(v);
}

interface FormState {
  displayName: string;
  maxContext: string;
  enabled: boolean;
  alternative: string;
  input: string;
  output: string;
  cacheRead: string;
  cacheWrite: string;
  tierAt: string;
  tierIn: string;
  tierOut: string;
  tierCacheRead: string;
  caps: Capabilities;
}

function fromModel(m: AdminModel): FormState {
  return {
    displayName: m.displayName,
    maxContext: str(m.maxContextTokens),
    enabled: m.enabled,
    alternative: m.alternativeModelId != null ? String(m.alternativeModelId) : '',
    input: String(m.inputCostPerMillionUsd),
    output: String(m.outputCostPerMillionUsd),
    cacheRead: str(m.cacheReadCostPerMillionUsd),
    cacheWrite: str(m.cacheWriteCostPerMillionUsd),
    tierAt: str(m.longContextThresholdTokens),
    tierIn: str(m.longContextInputCostPerMillionUsd),
    tierOut: str(m.longContextOutputCostPerMillionUsd),
    tierCacheRead: str(m.longContextCacheReadCostPerMillionUsd),
    caps: {
      images: m.supportsImageInput,
      documents: m.supportsDocumentInput,
      fileTasks: m.supportsFileTasks,
      effort: m.supportsEffort,
      effortWithTools: m.supportsEffortWithTools
    }
  };
}

function ModelEditor({ model, all, isDefault, tab, onTab }: {
  model: AdminModel;
  all: AdminModel[];
  isDefault: boolean;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const decisions = isDecisionsModel(model);
  const initial = fromModel(model);
  const [form, setForm] = useState<FormState>(initial);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm(f => ({ ...f, [key]: value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const altId = form.alternative === '' ? null : Number(form.alternative);

  const tierFields = [form.tierAt, form.tierIn, form.tierOut, form.tierCacheRead];
  const tierPartial = tierFields.some(v => v.trim() !== '') && [form.tierAt, form.tierIn, form.tierOut].some(v => v.trim() === '');
  const badFields = [form.input, form.output, form.cacheRead, form.cacheWrite, form.maxContext, ...tierFields].some(badNumber);
  const nameMissing = form.displayName.trim() === '';
  const invalid = badFields || tierPartial || nameMissing;

  /**
   * One Save for the whole page. The catalogue fields go through the upsert with the enabled
   * state the server already has; a change to Enabled then goes through the enable endpoint,
   * which is what sets or clears the deactivation marker and carries the fallback when disabling.
   */
  const save = useMutation({
    mutationFn: async () => {
      const saved = await api.adminUpsertModel({
        supplier: model.supplier,
        modelId: model.modelId,
        displayName: form.displayName.trim(),
        enabled: model.enabled,
        kind: model.kind,
        inputCostPerMillionUsd: parsePrice(form.input),
        outputCostPerMillionUsd: parsePrice(form.output),
        cacheReadCostPerMillionUsd: parseOptional(form.cacheRead),
        cacheWriteCostPerMillionUsd: parseOptional(form.cacheWrite),
        longContextThresholdTokens: parseOptional(form.tierAt),
        longContextInputCostPerMillionUsd: parseOptional(form.tierIn),
        longContextOutputCostPerMillionUsd: parseOptional(form.tierOut),
        longContextCacheReadCostPerMillionUsd: parseOptional(form.tierCacheRead),
        maxContextTokens: parseOptional(form.maxContext),
        supportsImageInput: form.caps.images,
        supportsDocumentInput: form.caps.documents,
        supportsFileTasks: form.caps.fileTasks,
        supportsEffort: form.caps.effort,
        supportsEffortWithTools: form.caps.effortWithTools,
        alternativeModelId: altId
      });
      if (form.enabled === model.enabled) return saved;
      return api.adminSetModelEnabled(model.id, {
        enabled: form.enabled,
        alternativeModelId: form.enabled ? undefined : altId
      });
    },
    // Reset the form to what the server stored (it may normalise what was typed, e.g. "3.0" -> 3),
    // so a clean save leaves nothing marked unsaved.
    onSuccess: saved => {
      qc.setQueryData<AdminModel[]>(['admin-models'], prev => prev?.map(m => (String(m.id) === String(saved.id) ? saved : m)));
      setForm(fromModel(saved));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin-models'] })
  });

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.adminDeleteModel(model.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-models'] });
      navigate('/admin/models');
    }
  });

  const fallbackOptions = all.filter(a => String(a.id) !== String(model.id) && a.kind === model.kind);
  const currentFallback = fallbackOptions.find(a => String(a.id) === form.alternative);
  const tabs: Tab[] = ['overview', 'pricing', 'usage'];

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = tabs.indexOf(tab);
    let next: Tab | null = null;
    if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    onTab(next);
    document.getElementById(`model-tab-${next}`)?.focus();
  };

  const numField = (id: string, label: string, key: keyof FormState, opts?: { step?: number; placeholder?: string; hint?: string }) => {
    const value = form[key] as string;
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <input id={id} type="number" min={0} step={opts?.step ?? 0.01} placeholder={opts?.placeholder}
          value={value} onChange={e => set(key, e.target.value as never)} aria-invalid={badNumber(value) || undefined} />
        {badNumber(value) && <div className="field-error">Enter a number of 0 or more.</div>}
        {opts?.hint && <div className="hint readable">{opts.hint}</div>}
      </div>
    );
  };

  return (
    <>
      <div className="crumbs">
        Administration / <Link to="/admin/models">Models</Link> / {model.displayName}
      </div>

      <div className="detail-head">
        <div style={{ minWidth: 0 }}>
          <h1>
            {model.displayName}
            {decisions ? <DecisionsBadge /> : <span className="pill pill-kind pill-sm">LLM</span>}
            {isDefault && <span className="pill pill-success pill-sm">Gateway default</span>}
            {model.deactivatedAtUtc && (
              <span className={`pill pill-sm ${needsAttention(model) ? 'pill-attention' : 'pill-disabled'}`}>
                Deactivated{needsAttention(model) ? ' · no fallback' : ''}
              </span>
            )}
          </h1>
          <div className="detail-id"><span className="mono">{model.modelId}</span> · via {model.supplier}</div>
        </div>
        <div className="detail-actions">
          <label className="switch-label">
            <span className="switch">
              <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
              <span className="track"></span>
            </span>
            Enabled for callers
          </label>
          {dirty && <span className="dirty-dot" role="status">Unsaved changes</span>}
          {dirty && (
            <button type="button" className="btn-ghost" onClick={() => { setForm(initial); save.reset(); }}
              disabled={save.isPending}>
              Discard
            </button>
          )}
          <button type="button" className="btn-primary" onClick={() => save.mutate()}
            disabled={!dirty || invalid || save.isPending}
            title={invalid ? 'Fix the highlighted fields first' : undefined}>
            {save.isPending ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {save.error && <div className="error-banner" role="alert">Could not save: {apiErrorMessage(save.error)}</div>}
      {invalid && dirty && (
        <div className="error-banner" role="alert">
          {nameMissing ? 'The display name can\'t be empty. ' : ''}
          {tierPartial ? 'The long-context tier is half set: set the threshold and both rates, or clear all of them (Pricing tab). ' : ''}
          {badFields ? 'A number field holds something that isn\'t a number of 0 or more.' : ''}
        </div>
      )}
      {!form.enabled && model.enabled && (
        <div className="info-banner">
          Switching this model off: requests for it {currentFallback
            ? <>will be served by <strong>{currentFallback.displayName}</strong>.</>
            : <>will fail, because no fallback is set (Overview, "If this model is switched off").</>}
        </div>
      )}

      <div className="tabs" role="tablist" aria-label="Model sections">
        {tabs.map(t => (
          <button key={t} id={`model-tab-${t}`} type="button" role="tab" className="tab"
            aria-selected={tab === t} aria-controls={`model-panel-${t}`} tabIndex={tab === t ? 0 : -1}
            onClick={() => onTab(t)} onKeyDown={onTabKey}>
            {tabLabels[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div id="model-panel-overview" role="tabpanel" aria-labelledby="model-tab-overview" className="detail-grid">
          <div className="detail-col">
            {decisions && (
              <div className="note-violet">
                <div>
                  <strong>This model makes decisions, it doesn't write text.</strong> Callers send input and
                  questions to <code>POST /api/v1/decisions</code> and get back a choice, yes/no or score with
                  probabilities. It's never offered for chat, and chat-only settings are hidden here.
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-header"><h3>What it accepts</h3></div>
              <div className="card-body">
                {decisions ? (
                  <>
                    <div className="kv"><span>Input</span><span>Text and JSON</span></div>
                    <div className="kv">
                      <span>Images</span>
                      <span>{model.supportsImageInput ? 'Yes, as image parts in the input' : 'No. The model reads text only'}</span>
                    </div>
                    <div className="kv">
                      <span>Input limit</span>
                      <span>{model.maxContextTokens != null ? `${fmtNumber(model.maxContextTokens)} tokens` : 'Not recorded'}</span>
                    </div>
                    <div className="kv"><span>Question types</span><span>Choice · Yes/no · Score</span></div>
                    <p className="text-muted" style={{ fontSize: '.84rem', marginTop: 8 }}>
                      Tools, effort, PDFs and file tasks don't apply to Decisions models.
                    </p>
                  </>
                ) : (
                  <CapabilitySwitches value={form.caps} onChange={v => set('caps', v)} idPrefix={`cap-${model.id}`} />
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>If this model is switched off</h3></div>
              <div className="card-body">
                <div className="field" style={{ marginBottom: 6 }}>
                  <label htmlFor="fallback">Serve this model instead</label>
                  <select id="fallback" value={form.alternative} onChange={e => set('alternative', e.target.value)}>
                    <option value="">None — callers get an error</option>
                    {fallbackOptions.map(a => (
                      <option key={a.id} value={String(a.id)}>
                        {a.supplier}/{a.displayName}{a.enabled ? '' : ' (disabled)'}
                      </option>
                    ))}
                  </select>
                  <div className="hint readable">
                    Only other {decisions ? 'Decisions models' : 'language models'} can be picked here. Callers asking
                    for this model get the fallback's answer, with a note saying it was substituted.
                  </div>
                </div>
                {model.deactivatedAtUtc && (
                  <div className={needsAttention(model) ? 'warn-text' : 'text-muted'} style={{ fontSize: '.86rem', marginTop: 8 }}>
                    Deactivated {fmtDate(model.deactivatedAtUtc)}{model.deactivationReason ? `: ${model.deactivationReason}` : ''}.
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Remove</h3></div>
              <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="text-muted" style={{ flex: 1, minWidth: 200, fontSize: '.9rem' }}>
                  Deletes it from the catalogue. Past usage stays in reports.
                </div>
                {confirmingDelete ? (
                  <>
                    <button type="button" className="btn-danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
                      {remove.isPending ? 'Deleting...' : `Delete ${model.displayName}`}
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={remove.isPending}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-danger" onClick={() => setConfirmingDelete(true)}>Delete model</button>
                )}
                {remove.error && <div className="field-error" role="alert" style={{ flexBasis: '100%' }}>{apiErrorMessage(remove.error)}</div>}
              </div>
            </div>
          </div>

          <div className="detail-col">
            <div className="card">
              <div className="card-header">
                <h3>Price</h3>
                <button type="button" className="link-btn" onClick={() => onTab('pricing')}>Edit</button>
              </div>
              <div className="card-body">
                <div className="kv"><span>Input, per 1M tokens</span><strong>{fmtUsd(model.inputCostPerMillionUsd)}</strong></div>
                <div className="kv">
                  <span>Output</span>
                  <span>{decisions ? 'Free, not billed' : fmtUsd(model.outputCostPerMillionUsd)}</span>
                </div>
                {!decisions && model.cacheReadCostPerMillionUsd != null && (
                  <div className="kv"><span>Cache read</span><span>{fmtUsd(model.cacheReadCostPerMillionUsd)}</span></div>
                )}
                {model.longContextThresholdTokens != null && (
                  <div className="kv">
                    <span>Above {fmtNumber(model.longContextThresholdTokens)} tokens</span>
                    <span>
                      {fmtUsd(model.longContextInputCostPerMillionUsd)}
                      {decisions ? '' : ` / ${fmtUsd(model.longContextOutputCostPerMillionUsd)}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Name and limits</h3></div>
              <div className="card-body">
                <div className="field">
                  <label htmlFor="display-name">Display name</label>
                  <input id="display-name" type="text" value={form.displayName}
                    onChange={e => set('displayName', e.target.value)} aria-invalid={nameMissing || undefined} />
                  {nameMissing && <div className="field-error">A display name is required.</div>}
                </div>
                {numField('max-context', decisions ? 'Input limit (tokens)' : 'Context window (tokens)', 'maxContext',
                  { step: 1, placeholder: 'Not recorded' })}
              </div>
            </div>

            <UsageSummary model={model} compact onMore={() => onTab('usage')} />

            <div className="text-muted" style={{ fontSize: '.84rem' }}>
              Last changed {fmtDate(model.updatedAtUtc)}{model.updatedBy ? ` by ${model.updatedBy}` : ''}.
            </div>
          </div>
        </div>
      )}

      {tab === 'pricing' && (
        <div id="model-panel-pricing" role="tabpanel" aria-labelledby="model-tab-pricing" className="detail-grid">
          <div className="detail-col">
            <div className="card">
              <div className="card-header"><h3>Price per million tokens (USD)</h3></div>
              <div className="card-body">
                <div className="price-grid">
                  {numField('price-input', 'Input', 'input')}
                  {decisions
                    ? <div className="field"><label>Output</label><div style={{ padding: '8px 0' }}>Free, not billed</div></div>
                    : numField('price-output', 'Output', 'output')}
                  {!decisions && numField('price-cache-read', 'Cache read', 'cacheRead',
                    { placeholder: 'Same as input', hint: 'Blank falls back to the input rate.' })}
                  {!decisions && numField('price-cache-write', 'Cache write', 'cacheWrite',
                    { placeholder: 'Same as input', hint: 'Blank falls back to the input rate.' })}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Long-context tier</h3></div>
              <div className="card-body">
                <p className="text-muted" style={{ fontSize: '.88rem', marginBottom: 12 }}>
                  Once a prompt passes the threshold the supplier charges these rates for the whole call, output
                  included. Leave all blank for one flat price.
                </p>
                <div className="price-grid">
                  {numField('tier-at', 'Higher rates above (tokens)', 'tierAt', { step: 1000, placeholder: 'e.g. 200000' })}
                  {numField('tier-in', 'Input above the threshold', 'tierIn')}
                  {numField('tier-out', decisions ? 'Output above the threshold (not billed, enter 0)' : 'Output above the threshold', 'tierOut')}
                  {numField('tier-cr', 'Cache read above the threshold', 'tierCacheRead', { placeholder: 'Optional' })}
                </div>
                {tierPartial && (
                  <div className="field-error" role="alert">
                    Set the threshold and both rates, or clear all of them — a partial tier prices nothing correctly.
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="detail-col">
            <div className="text-muted" style={{ fontSize: '.88rem' }}>
              Prices are what the gateway charges callers' budgets. Check them against the supplier's pricing page
              after any supplier price change. Changes apply once you press Save changes.
            </div>
          </div>
        </div>
      )}

      {tab === 'usage' && (
        <div id="model-panel-usage" role="tabpanel" aria-labelledby="model-tab-usage">
          <UsageSummary model={model} />
        </div>
      )}
    </>
  );
}

/**
 * Last 30 days for this model, from the gateway's by-model usage summary (the same call the
 * Gateway usage page makes). It gives totals only — no per-caller split for one model.
 */
function UsageSummary({ model, compact, onMore }: { model: AdminModel; compact?: boolean; onMore?: () => void }) {
  const api = useApi();
  const from = daysAgo(30);
  const to = isoDate(new Date());
  const byModel = useQuery({
    queryKey: ['admin-usage-by-model', from, to, ''],
    queryFn: () => api.adminUsageByModel(from, to)
  });
  const row = (byModel.data ?? []).find(r => r.group === `${model.supplier}/${model.modelId}`);
  const callsLabel = isDecisionsModel(model) ? 'Decisions' : 'Calls';

  const body = byModel.isLoading ? (
    <div className="text-muted">Loading usage...</div>
  ) : byModel.error ? (
    <div className="error-banner" style={{ marginBottom: 0 }}>Could not load usage: {apiErrorMessage(byModel.error)}</div>
  ) : !row ? (
    <div className="text-muted">No calls in the last 30 days.</div>
  ) : compact ? (
    <>
      <div className="kv"><span>{callsLabel}</span><span>{fmtNumber(row.calls)}</span></div>
      <div className="kv"><span>Cost</span><span>{fmtUsd(row.costUsd)}</span></div>
    </>
  ) : (
    <div className="usage-tiles">
      <div className="stat"><div className="stat-label">{callsLabel}</div><div className="stat-value">{fmtNumber(row.calls)}</div></div>
      <div className="stat"><div className="stat-label">Input tokens</div><div className="stat-value">{fmtTokens(row.inputTokens)}</div></div>
      <div className="stat"><div className="stat-label">Output tokens</div><div className="stat-value">{fmtTokens(row.outputTokens)}</div></div>
      <div className="stat"><div className="stat-label">Cost</div><div className="stat-value">{fmtUsd(row.costUsd)}</div></div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h3>Last 30 days</h3>
        {compact
          ? <button type="button" className="link-btn" onClick={onMore}>More</button>
          : <Link to="/admin/usage">Gateway usage</Link>}
      </div>
      <div className="card-body">
        {body}
        {!compact && (
          <p className="text-muted" style={{ fontSize: '.84rem', marginTop: 4 }}>
            Totals across all callers. For a per-caller or per-day breakdown, use Gateway usage.
          </p>
        )}
      </div>
    </div>
  );
}
